import type { ReactNode } from 'react'

import { StatusPill, type StatusTone } from './StatusPill'

/**
 * One record as a card (M23-T03), drawn on the Emergency, Leads and Medication
 * frames: a serif title, a reference, an age and status top-right, the person's
 * own words in quotes, a grid of labelled facts, a rule, then an action row.
 *
 * `facts` is a **variable-length list on purpose**. Emergency draws four, Leads
 * three, and Medication draws four of which two cannot ship — `TRANSPORT` has
 * no field behind it and `Ready in 2hrs` no readiness estimate (M23 gaps
 * A1–A3). A card that hard-coded four columns would invite padding the gap with
 * something invented, which is the exact failure the gap register exists to
 * prevent. Three facts is a finished card.
 */
export type Fact = { label: string; value: ReactNode }

export function RecordCard({
  title,
  reference,
  age,
  status,
  statusTone,
  statusLabel,
  quote,
  facts,
  actions,
  children,
}: {
  title: string
  reference?: string
  age?: string
  status?: string
  statusTone?: StatusTone
  statusLabel?: string
  /** The caller's own words. Omitted entirely when absent — never empty quotes. */
  quote?: string | null
  facts: readonly Fact[]
  actions?: ReactNode
  /** Anything below the facts and above the actions (offers, a nested list). */
  children?: ReactNode
}) {
  return (
    <article className="rounded-2xl border border-line-1 bg-bg-app px-6 py-5">
      <header className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="font-display text-xl font-semibold text-ink-1">{title}</h2>
          {reference !== undefined && (
            <p className="mt-0.5 text-sm text-ink-3">{reference}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {age !== undefined && <span className="text-sm text-ink-3">{age}</span>}
          {status !== undefined && (
            <StatusPill
              status={status}
              {...(statusTone !== undefined && { tone: statusTone })}
              {...(statusLabel !== undefined && { label: statusLabel })}
            />
          )}
        </div>
      </header>

      {/* Nullable in every source. A record without one renders without the
          line, not with a pair of empty quote marks. */}
      {quote !== undefined && quote !== null && quote !== '' && (
        <p className="mt-4 text-base text-ink-1">“{quote}”</p>
      )}

      {facts.length > 0 && (
        <dl className="mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          {facts.map((fact) => (
            <div key={fact.label}>
              <dt className="text-xs uppercase tracking-wide text-ink-3">
                {fact.label}
              </dt>
              <dd className="mt-1 text-sm text-ink-1">{fact.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {children}

      {actions !== undefined && (
        <>
          <hr className="mt-5 border-line-1" />
          <div className="flex flex-wrap items-center gap-3 pt-4">{actions}</div>
        </>
      )}
    </article>
  )
}
